import React, { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export function DarkKnightCharacterOverlay() {
  const shouldReduceMotion = useReducedMotion();
  const [batSignalActive, setBatSignalActive] = useState(true);
  const [jokerPrankActive, setJokerPrankActive] = useState(false);
  const [activeGadget, setActiveGadget] = useState<string | null>(null);

  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {/* Animated Bat-Signal Beam Sweeping Across the Night Sky */}
      {batSignalActive && (
        <motion.div
          animate={
            shouldReduceMotion
              ? {}
              : {
                  rotate: [-8, 8, -8],
                  opacity: [0.7, 0.95, 0.7],
                }
          }
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-96 h-[800px] pointer-events-none origin-top z-0"
          style={{
            background:
              'conic-gradient(from 180deg at 50% 0%, transparent 155deg, rgba(255, 215, 0, 0.4) 180deg, transparent 205deg)',
          }}
        />
      )}

      {/* Bat-Signal Online / Offline Toggle */}
      <motion.div
        animate={shouldReduceMotion ? {} : { y: [0, -6, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-12 right-6 md:right-14 pointer-events-auto z-30"
      >
        <button
          onClick={() => setBatSignalActive(!batSignalActive)}
          className="flex items-center gap-2 rounded-2xl border border-yellow-400/60 bg-black/90 px-4 py-2 text-xs font-bold text-yellow-300 backdrop-blur-md transition-all hover:bg-yellow-950/60 cursor-pointer shadow-lg shadow-yellow-500/30"
        >
          <svg viewBox="0 0 100 50" className="w-7 h-4 fill-yellow-400">
            <path d="M 50 5 C 35 5 20 15 5 25 C 20 35 35 40 50 35 C 65 40 80 35 95 25 C 80 15 65 5 50 5 Z" />
          </svg>
          {batSignalActive ? 'BAT-SIGNAL: ONLINE 🦇' : 'ACTIVATE BAT-SIGNAL'}
        </button>
      </motion.div>

      {/* Joker's Surprise Birthday Prank Trigger Button */}
      <motion.div
        animate={shouldReduceMotion ? {} : { scale: [1, 1.05, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-12 left-6 md:left-14 pointer-events-auto z-30"
      >
        <button
          onClick={() => {
            setJokerPrankActive(true);
            setTimeout(() => setJokerPrankActive(false), 3500);
          }}
          className="flex items-center gap-2 rounded-2xl border border-purple-500/80 bg-purple-950/90 px-4 py-2 text-xs font-black text-purple-200 backdrop-blur-md transition-all hover:bg-purple-900 cursor-pointer shadow-lg shadow-purple-500/40"
        >
          <span className="text-lg">🤡</span>
          <span>JOKER'S CAKE SURPRISE!</span>
        </button>
      </motion.div>

      {/* Joker Cake Surprise Pop-up Modal Story */}
      {jokerPrankActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-40"
        >
          <div className="rounded-3xl border-4 border-yellow-400 bg-black/95 p-8 text-center shadow-2xl shadow-purple-500/60 max-w-md mx-4 border-dashed">
            <span className="text-5xl animate-bounce inline-block mb-3">🤡 🎂 🦇</span>
            <h3 className="text-2xl font-black text-yellow-400 uppercase tracking-wider">
              HAHAHA! JOKER STOLE THE CANDLES!
            </h3>
            <p className="text-sm font-semibold text-slate-200 mt-2">
              Batman arrives with the Batarang sparkler to light the Gotham birthday cake!
            </p>
            <div className="mt-4 inline-block rounded-full bg-yellow-400 px-6 py-2 text-xs font-black text-black uppercase">
              100% GOTHAM HERO APPROVED 🎉
            </div>
          </div>
        </motion.div>
      )}

      {/* Interactive Gadget Armory (Batarang, Grapple, Batmobile) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-3 rounded-3xl border border-yellow-400/40 bg-black/90 px-6 py-3 backdrop-blur-lg z-30 shadow-xl shadow-yellow-500/20">
        {[
          { id: 'batarang', name: 'Batarang Strike', icon: '🦇' },
          { id: 'batmobile', name: 'Batmobile Turbo', icon: '🏎️' },
          { id: 'joker_truce', name: 'Joker Party Truce', icon: '🤡' },
        ].map((gadget) => (
          <button
            key={gadget.id}
            onClick={() => setActiveGadget(activeGadget === gadget.id ? null : gadget.id)}
            className="flex flex-col items-center gap-1 text-xs text-yellow-300 transition-all hover:scale-110 cursor-pointer"
          >
            <span className="text-2xl">{gadget.icon}</span>
            <span className="text-[10px] font-bold text-slate-200 uppercase">{gadget.name}</span>
          </button>
        ))}
      </div>

      {/* Gadget Deployment Story Banner */}
      {activeGadget && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-24 left-1/2 -translate-x-1/2 pointer-events-none z-40"
        >
          <div className="rounded-2xl border border-yellow-400 bg-black/95 px-8 py-4 text-center shadow-2xl shadow-yellow-500/40">
            <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">GOTHAM HERO DEFENSE</p>
            <h4 className="text-lg font-black text-white mt-1 uppercase">{activeGadget.replace('_', ' ')} ACTIVATED! ⚡</h4>
          </div>
        </motion.div>
      )}
    </div>
  );
}
